import React, { useEffect, useState } from "react";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { listLabComments, postLabComment } from "../lib/api";

const ROLE_STYLES = {
  client: { label: "Member", color: "bg-[var(--lc-cream)] text-[var(--lc-ink)] border-[var(--lc-line)]" },
  doctor: { label: "Doctor", color: "bg-[var(--lc-moss)]/15 text-[var(--lc-moss-dark)] border-[var(--lc-moss)]/40" },
  admin: { label: "Coach", color: "bg-amber-100 text-amber-900 border-amber-200" },
};

export default function LabCommentThread({ labId, auth, placeholder = "Add a comment..." }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setComments(await listLabComments(labId, auth));
    } catch (err) {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setPosting(true);
    setError("");
    try {
      await postLabComment(labId, draft.trim(), auth);
      setDraft("");
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not post comment");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="mt-3 border-t border-[var(--lc-line)] pt-3" data-testid={`lab-comments-${labId}`}>
      <div className="eyebrow text-[var(--lc-muted)] mb-2 flex items-center gap-1.5">
        <MessageSquare className="h-3 w-3" /> Comments
        {comments.length > 0 && <span className="text-[var(--lc-ink)]">· {comments.length}</span>}
      </div>

      {loading ? (
        <div className="text-xs text-[var(--lc-muted)] inline-flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading...
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid={`lab-comments-list-${labId}`}>
          {comments.map((c) => {
            const style = ROLE_STYLES[c.author_role] || ROLE_STYLES.client;
            return (
              <li key={c.id} className="flex gap-3" data-testid={`lab-comment-${c.id}`}>
                <div className="flex-shrink-0 pt-0.5">
                  <span className={`inline-block text-[9px] tracking-[0.14em] uppercase font-bold px-2 py-1 border rounded-sm ${style.color}`}>
                    {style.label}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[var(--lc-ink)] whitespace-pre-wrap">{c.text}</div>
                  <div className="text-[10px] text-[var(--lc-muted)] mt-0.5">
                    {c.author_name} · {new Date(c.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <form onSubmit={submit} className="mt-3 flex gap-2" data-testid={`lab-comment-form-${labId}`}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)]"
          data-testid={`lab-comment-input-${labId}`}
        />
        <button
          type="submit"
          disabled={!draft.trim() || posting}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-[var(--lc-ink)] text-white text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)] disabled:opacity-50"
          data-testid={`lab-comment-submit-${labId}`}
        >
          {posting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          Send
        </button>
      </form>
      {error && (
        <div className="mt-2 text-xs text-red-700" data-testid={`lab-comment-error-${labId}`}>
          {error}
        </div>
      )}
    </div>
  );
}
